import React from 'react';
import styled from 'styled-components';
import { CloseOutlined, EyeOutlined, StarFilled, TeamOutlined, UserSwitchOutlined } from '@ant-design/icons';
import { types } from './helper';
import { Button } from 'antd';
import { Link } from 'react-router-dom';
import { countries } from 'countries-list';
import { getProgress } from '../helpers';
import { openDSWindow } from '../../../../helpers/Window';

const Style = styled.div`
  width: 270px;
  border-left: 1px solid #CCCCCC;
  overflow: hidden;

  label {
    margin-bottom: 0px;
  }

  .body {
    gap: 14px;
    padding: 14px;
    flex: 1;
    max-height: 460px;
    overflow-y: auto;

    .info {
      display: flex;
      gap: 10px;

      .own-data-type {
        font-size: 12px;
      }

      .default-type {
        display: flex;
        gap: 4px;
        margin-top: 3px;

        svg {
          font-size: 18px;
        }

        label {
          margin-top: 1px;
          font-size: 12px;
          margin-bottom: 0px;
          font-weight: 700;
        }
      }

      .id {
        color: var(--color-primary-70);
        font-size: var(--font-size-lg);
        font-weight: 500;
      }
    }

    .label {
      font-size: 20px;
    }

    .data {
      margin-top: 10px;
      gap: 14px;

      section {
        gap: 14px 30px;
      }

      .data-label {
        font-size: 14px;
        font-weight: 700;
      }

      .data-item {
        gap: 2px;

        label {
          font-size: 12px;
          color: #6B6B6B;
        }

        span {
          font-weight: 500;
          font-size: 14px;
          color: #001529;

          &.percentage {
            font-weight: 700;
            font-size: 20px;
            color: var(--color-primary-70);
          }
        }
      }
    }

    .sources {
      gap: 8px;
      margin-top: 10px;

      .items {
        .item {
          display: flex;
          padding: 10px 0px;
          border-top: 1px solid #F0F0F0;

          &:last-of-type {
            border-bottom: 1px solid #F0F0F0;
          }

          .label {
            flex: 1;
            display: flex;
            gap: 6px;
            margin-top: 2px;

            svg {
              font-size: 14px;
              color: #FFC700;
              margin-top: 4px;
            }

            .circle {
              width: 12px;
              height: 12px;
              border-radius: 100%;
              margin-top: 4px;

              &.supply-chain-actor {
                background: #52C41A;
              }

              &.other-info-source {
                background: #FF7875;
              }
            }

            label {
              color: #001529;
              font-size: 14px;
            }
          }

          button {
            svg {
              margin-top: -3px;
            }
          }
        }
      }
    }
  }
`;

const dataTypes = {
  DEFUALT: 'DEFUALT',
  PERCENTAGE: 'PERCENTAGE',
  COUNTRY: 'COUNTRY',
  LEGAL_FORM: 'LEGAL_FORM',
};

const dataConfig = [
  {
    country: {
      type: dataTypes.COUNTRY,
      label: 'Country',
    },
    legalForm: {
      type: dataTypes.LEGAL_FORM,
      label: 'Legal Form',
    },
  },
  {
    holding: {
      type: dataTypes.PERCENTAGE,
      label: 'Holding',
    }
  },
];

export default function Sider({
  isCooperative,
  element,
  t,
  clear = () => { },
  sources = { featuredData: [], supplyChainActor: [], otherInformationSources: [] },
  redirectToView = () => { },
  getOption = () => { },
  isSingleSource = false,
  selectedSource = null,
  mode,
}) {
  const isBeneficary = () => element.config.type === types.BENEFICIARIES;
  const isStakeholder = () => element.config.type === types.STAKEHOLDER;

  const renderInfo = () => (
    <div className="info">
      {isBeneficary() && (
        <div className="default-type">
          <UserSwitchOutlined />
          <label>{t('Beneficiary')}</label>
        </div>
      )}
      {isStakeholder() && (
        <div className="default-type">
          <TeamOutlined />
          <label>{t(isCooperative ? 'Member' : 'Shareholder')}</label>
        </div>
      )}
      <label className="id">{element.config.itemId || element.id}</label>
    </div>
  );

  const renderData = () => dataConfig.map((s, i) => (
    <section key={`section-${i + 1}`}>
      {Object.keys(s).map((k, i) => {
        const config = s[k];
        let show = (element.config.data);
        if (show && k === 'holding') {
          if (element.config.type !== 'STAKEHOLDERS') {
            show = false;
          } else if (element.config.level < -1) {
            show = false;
          }
        }
        if (show) {
          if (typeof element.config.data[k] === 'string' || typeof element.config.data[k] === 'number') {
            return (
              <div className="data-item default-content" key={`item-${i + 1}`}>
                <label>{t(config.label)}</label>
                {config.type === dataTypes.DEFUALT && (
                  <span>{element.config.data[k]}</span>
                )}
                {config.type === dataTypes.LEGAL_FORM && (
                  <span>{getOption('stakeholderSubCategoriesOptions', element.config.data[k])}</span>
                )}
                {config.type === dataTypes.COUNTRY && (
                  <span>{element.config.data[k] ? countries[element.config.data[k]].name : '--'}</span>
                )}
                {config.type === dataTypes.PERCENTAGE && (
                  <span className="percentage">{getProgress(element.config.data[k])}%</span>
                )}
              </div>
            );
          }
        }
        return null;
      })}
    </section>
  ));

  const renderSources = () => {
    const currentSources = element.config.sources;
    if (currentSources) {
      let allSources = [];
      currentSources.forEach((elS) => {
        const findFD = (sources?.featuredData || []).find((d) => d.id === elS);
        if (findFD) {
          allSources.push({ ...findFD, type: 'FD' });
        } else {
          const findSA = (sources?.supplyChainActor || []).find((d) => d.id === elS);
          if (findSA) {
            allSources.push({ ...findSA, type: 'SA' });
          } else {
            const findOS = (sources?.otherInformationSources || []).find((d) => d.id === elS);
            if (findOS) {
              allSources.push({ ...findOS, type: 'OS' });
            }
          }
        }
      });

      return allSources.length ? (
        <div className="sources default-content">
          <label>{t('Available Sources')}</label>
          <div className="items default-content">
            {allSources.map((s) => (
              <div className="item" key={`sources-${s.id}`}>
                <div className="label">
                  {s.type === 'FD' && <StarFilled />}
                  {s.type === 'SA' && <div className="circle supply-chain-actor" />}
                  {s.type === 'OS' && <div className="circle other-info-source" />}
                  <label>{s.label}</label>
                </div>
                {mode === 'proxy' ? (
                  <Button
                    icon={<EyeOutlined />}
                    type="ghost"
                    size="small"
                    onClick={() => {
                      openDSWindow({
                        url: redirectToView({
                          custom: `/analysis/scp/${element.config.itemId || element.id}/${s.id}`
                        }),
                        name: `SCP source ${s.id}`,
                      })
                    }}
                  />
                ) : (
                  <Link
                    target="_blank"
                    rel="noopener noreferrer"
                    to={
                      redirectToView({
                        custom: `/analysis/scp/${element.config.itemId || element.id}/${s.id}`
                      })}
                  >
                    <Button icon={<EyeOutlined />} type="ghost" size="small" />
                  </Link>
                )}
              </div>
            ))}
          </div>
        </div >
      ) : null;
    }
    return null;
  };

  return (
    <Style className="default-content">
      <div className="sider-header">
        <label>{t('Details')}</label>
        <CloseOutlined
          className="beneficial-ownership-filter"
          onClick={clear}
        />
      </div>
      <div className="body default-content">
        {renderInfo()}
        <div className="label">
          <label style={{ fontWeight: 500 }}>{element.label}</label>
        </div>
        <div className="data default-content">
          {element.config.label && (
            <label className="data-label">
              {element.config.label}
              {' '}
              {t('Info')}
            </label>
          )}
          {renderData()}
          {!isSingleSource ? renderSources()
            : (
              <>
                {mode === 'proxy' ? (
                  <Button
                    style={{ flex: 1 }}
                    onClick={() => {
                      openDSWindow({
                        url: redirectToView({
                          custom: `/analysis/scp/${element.config.itemId || element.id}${selectedSource ? `/${selectedSource}` : ''}`,
                        }),
                        name: `SCP Analysis Source ${selectedSource}`,
                      })
                    }}
                  >
                    {t('View')}
                  </Button>
                ) : (
                  <Link
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{ display: 'flex' }}
                    to={redirectToView({
                      custom: `/analysis/scp/${element.config.itemId || element.id}${selectedSource ? `/${selectedSource}` : ''}`
                    })}>
                    <Button style={{ flex: 1 }}>
                      {t('View')}
                    </Button>
                  </Link>
                )}
              </>
            )}
        </div>
      </div>
    </Style>
  );
}
